import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { favoriteApi } from '@formosa/shared/api/im';
import { useChat } from '../../chat/ChatProvider';
import '../../styles/chat.css';

/** /chat/favorites — personal message favorites */
export default function FavoritesPage() {
  const navigate = useNavigate();
  const { openConversation, jumpToMessage } = useChat();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [hasMore, setHasMore] = useState(true);
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const load = useCallback((append = false) => {
    if (append) setLoadingMore(true);
    else {
      setLoading(true);
      setError('');
    }
    const cur = itemsRef.current;
    const beforeId = append && cur.length ? cur[cur.length - 1].id : undefined;
    favoriteApi
      .listMine({ beforeId, limit: 50 })
      .then((rows) => {
        const list = Array.isArray(rows) ? rows : [];
        setHasMore(list.length >= 50);
        setItems((prev) => (append ? [...prev, ...list] : list));
      })
      .catch((e) => setError(e?.message || '載入失敗'))
      .finally(() => {
        setLoading(false);
        setLoadingMore(false);
      });
  }, []);

  useEffect(() => {
    load(false);
  }, [load]);

  const openHit = async (row) => {
    if (!row?.conversationId || !row?.serverMessageId) return;
    const cid = String(row.conversationId);
    await openConversation(cid);
    await jumpToMessage(cid, {
      serverMessageId: row.serverMessageId,
      seq: row.seq,
    });
    navigate(`/chat/${cid}`);
  };

  const remove = async (row) => {
    if (!row?.conversationId || !row?.serverMessageId) return;
    try {
      await favoriteApi.remove(row.conversationId, row.serverMessageId);
      setItems((prev) => prev.filter((x) => x.serverMessageId !== row.serverMessageId));
    } catch (e) {
      setError(e?.message || '取消收藏失敗');
    }
  };

  return (
    <div className="chat-social-page">
      <header className="chat-social-page__header">
        <Link to="/chat" className="chat-link-btn">
          ← 聊天
        </Link>
        <h1>收藏訊息</h1>
        <button type="button" className="chat-link-btn" onClick={() => load(false)}>
          重新整理
        </button>
      </header>
      {error ? <p className="chat-state chat-state--error">{error}</p> : null}
      {loading && !items.length ? <p className="chat-state">載入中…</p> : null}
      {!loading && !items.length ? <p className="chat-state">尚無收藏</p> : null}
      {items.length ? (
        <ul className="chat-favorites-list">
          {items.map((row) => (
            <li key={row.id || row.serverMessageId} className="chat-favorites-list__item">
              <button type="button" className="chat-favorites-list__main" onClick={() => openHit(row)}>
                <span className="chat-favorites-list__type">{row.messageType || 'MSG'}</span>
                <span className="chat-favorites-list__text">
                  {row.previewText || row.serverMessageId}
                </span>
                <time className="chat-favorites-list__time">{formatTime(row.favoritedAt)}</time>
              </button>
              <button type="button" className="chat-link-btn" onClick={() => remove(row)}>
                取消
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {hasMore && items.length ? (
        <button
          type="button"
          className="chat-link-btn"
          disabled={loadingMore}
          onClick={() => load(true)}
        >
          {loadingMore ? '載入中…' : '載入更多'}
        </button>
      ) : null}
    </div>
  );
}

function formatTime(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString();
}
